import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { format, parseISO, isValid } from "date-fns";
import EmptyState from "./EmptyState";
import StatusBadge from "./StatusBadge";
import LoadingRows from "./LoadingRows";

// Action buttons component
const ActionButtons = ({
  item = {},
  onEdit = () => {},
  onDelete = () => {},
}) => {
  const handleEdit = (e) => {
    try {
      e?.stopPropagation();
      if (typeof onEdit === "function") onEdit(item);
    } catch (error) {}
  };

  const handleDelete = (e) => {
    try {
      e?.stopPropagation();
      if (typeof onDelete === "function") onDelete(item?.id);
    } catch (error) {}
  };

  if (!item?.id) return null;

  return (
    <div className="flex space-x-2">
      <button
        onClick={handleEdit}
        className="text-primaryGreen hover:text-primaryGreen/80 p-1 rounded transition-colors"
        title="Edit"
      >
        <EditIcon className="h-4 w-4" />
      </button>
      <button
        onClick={handleDelete}
        className="text-primaryRed hover:text-primaryRed/80 p-1 rounded transition-colors"
        title="Delete"
      >
        <DeleteIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

// Pagination component
const TablePagination = ({
  page = 1,
  pageSize = 20,
  totalCount = 0,
  onPageChange = () => {},
  itemName = "item",
}) => {
  const safePageSize = Math.max(1, pageSize);
  const safeTotalCount = Math.max(0, totalCount);
  const startIndex = safeTotalCount === 0 ? 0 : (page - 1) * safePageSize + 1;
  const endIndex = Math.min(page * safePageSize, safeTotalCount);
  const totalPages = Math.max(1, Math.ceil(safeTotalCount / safePageSize));

  const getPageNumbers = () => {
    try {
      const maxPagesToShow = 5;
      let startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

      if (endPage - startPage + 1 < maxPagesToShow && startPage > 1) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }

      return Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
      );
    } catch (error) {
      return [1];
    }
  };

  const handlePageChange = (newPage) => {
    try {
      if (typeof onPageChange === "function") {
        onPageChange(Math.max(1, Math.min(newPage, totalPages)));
      }
    } catch (error) {}
  };

  return (
    <div className="flex flex-col sm:flex-row border-t border-gray-200 justify-between items-center text-sm text-gray-600 py-4 px-4 sm:px-6">
      <div className="mb-2 sm:mb-0">
        Showing {safeTotalCount === 0 ? "0" : `${startIndex}-${endIndex}`} of{" "}
        {safeTotalCount} total
        {safeTotalCount === 1 ? ` ${itemName}` : ` ${itemName}s`}
      </div>
      <div className="flex items-center gap-2">
        <button
          className="p-1 w-8 h-8 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          onClick={() => handlePageChange(page - 1)}
          disabled={page <= 1}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {getPageNumbers().map((pageNum) => (
          <button
            key={pageNum}
            onClick={() => handlePageChange(pageNum)}
            className={`flex items-center justify-center w-8 h-8 rounded ${
              pageNum === page
                ? "bg-primaryGreen text-white"
                : "border border-gray-300 hover:bg-gray-50"
            }`}
          >
            {pageNum}
          </button>
        ))}

        <button
          className="p-1 w-8 h-8 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          onClick={() => handlePageChange(page + 1)}
          disabled={page >= totalPages}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

const DataTable = ({
  data = [],
  columns = [],
  onView = () => {},
  onEdit = () => {},
  onDelete = () => {},
  isLoading = false,
  searchQuery = "",
  page = 1,
  pageSize = 20,
  totalCount = 0,
  onPageChange = () => {},
  itemName = "item",
  isClickable = false,
  fieldMappings = {},
  customRenderers = {},
}) => {
  // Default field mappings - maintaining backward compatibility
  const defaultFieldMappings = {
    priceFields: ["price", "buyingPrice", "sellingPrice", "totalPrice"],
    dateFields: [
      "dateOfCreation",
      "expiryDate",
      "issuedAt",
      "expiresAt",
      "activatedAt",
    ],
    statusFields: ["status"],
    actionFields: ["actions"],
    serviceFields: ["services"],
    sittingFields: [
      "numberOfSittings",
      "gapBetweenSittings",
      "sittingsGapUnit",
    ],
  };

  // Merge with custom field mappings
  const mappings = {
    priceFields:
      fieldMappings.priceFields !== undefined
        ? fieldMappings.priceFields
        : defaultFieldMappings.priceFields,
    dateFields:
      fieldMappings.dateFields !== undefined
        ? fieldMappings.dateFields
        : defaultFieldMappings.dateFields,
    statusFields:
      fieldMappings.statusFields !== undefined
        ? fieldMappings.statusFields
        : defaultFieldMappings.statusFields,
    actionFields:
      fieldMappings.actionFields !== undefined
        ? fieldMappings.actionFields
        : defaultFieldMappings.actionFields,
    serviceFields:
      fieldMappings.serviceFields !== undefined
        ? fieldMappings.serviceFields
        : defaultFieldMappings.serviceFields,
    sittingFields:
      fieldMappings.sittingFields !== undefined
        ? fieldMappings.sittingFields
        : defaultFieldMappings.sittingFields,
  };

  // Validate inputs
  const hasData = Array.isArray(data) && data.length > 0;
  const hasColumns = Array.isArray(columns) && columns.length > 0;
  const columnCount = hasColumns ? columns.length : 0;

  const handleRowClick = (item) => {
    try {
      if (!isClickable || !item?.id || typeof onView !== "function") return;
      onView(item);
    } catch (error) {}
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      let date;
      if (typeof dateString === "string") {
        const parsed = parseISO(dateString);
        date = isValid(parsed) ? parsed : new Date(dateString);
      } else {
        date = new Date(dateString);
      }
      if (!isValid(date)) return "—";
      return format(date, "MMM dd, yyyy");
    } catch (error) {
      return "—";
    }
  };

  // Updated cell styles with proper whitespace handling
  const cellStyles = {
    header: "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap bg-gray-50",
    base: "px-4 py-4 whitespace-nowrap",
    text: "text-sm text-gray-900",
    firstColumn: "flex items-center justify-between gap-2",
  };

  const renderCell = (item, column, isFirstColumn) => {
    try {
      if (!item || !column?.key) return "N/A";

      const value = column.path
        ? column.path.split(".").reduce((o, i) => (o ? o[i] : null), item)
        : item[column.key];

      // Check for custom renderer first
      if (
        customRenderers[column.key] &&
        typeof customRenderers[column.key] === "function"
      ) {
        return customRenderers[column.key](value, item);
      }

      if (mappings.statusFields.includes(column.key)) {
        return <StatusBadge status={value} />;
      }

      if (mappings.actionFields.includes(column.key)) {
        return (
          <ActionButtons item={item} onEdit={onEdit} onDelete={onDelete} />
        );
      }

      if (mappings.priceFields.includes(column.key)) {
        const price =
          typeof value === "number" ? value : parseFloat(value) || 0;
        return <div className={cellStyles.text}>₹{price}</div>;
      }

      if (mappings.dateFields.includes(column.key)) {
        return (
          <div className={`${cellStyles.text} font-medium text-gray-900`}>
            {formatDate(value) || "N/A"}
          </div>
        );
      }

      if (mappings.serviceFields.includes(column.key)) {
        const serviceCount = Array.isArray(value) ? value.length : 0;
        return (
          <div className={`${cellStyles.text} text-gray-500`}>
            {serviceCount} services
          </div>
        );
      }

      if (mappings.sittingFields.includes(column.key)) {
        if (column.key === "numberOfSittings") {
          return (
            <div className={`${cellStyles.text} text-gray-900`}>
              {value || "N/A"}
            </div>
          );
        }
        if (column.key === "gapBetweenSittings") {
          const sittingsGapUnit = item?.sittingsGapUnit;
          const unitText =
            sittingsGapUnit === "months"
              ? "M"
              : sittingsGapUnit === "weeks"
              ? "W"
              : "";
          return (
            <div className={`${cellStyles.text} text-gray-900`}>
              {value ? `${value}${unitText}` : "N/A"}
            </div>
          );
        }
      }

      // First column with chevron for clickable rows
      if (isFirstColumn) {
        return (
          <div className={cellStyles.firstColumn}>
            <span className={`${cellStyles.text} font-medium text-gray-900`}>
              {value || "N/A"}
            </span>
            {isClickable && (
              <ChevronRightIcon className="h-4 w-4 text-gray-400" />
            )}
          </div>
        );
      }

      return (
        <div className={`${cellStyles.text} font-medium text-gray-900`}>
          {value || "N/A"}
        </div>
      );
    } catch (error) {
      return "Error";
    }
  };

  if (!hasColumns) {
    return (
      <div className="text-center p-4 text-gray-500">No columns defined</div>
    );
  }

  return (
    <div className="w-full bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full">
      {/* Scrollable table container */}
      <div className="overflow-x-auto overflow-y-auto flex-1 min-h-0">
        <table className="w-full table-auto">
          <thead className="sticky top-0 z-10">
            <tr>
              {columns.map((column, index) => (
                <th key={column?.key || index} className={cellStyles.header}>
                  <div className="flex items-center gap-1">
                    {column?.label || ""}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <LoadingRows columnCount={columnCount} rowCount={pageSize} />
            ) : !hasData ? (
              <EmptyState
                isLoading={isLoading}
                searchQuery={searchQuery}
                itemName={itemName}
                columnCount={columnCount}
              />
            ) : (
              data.map((item) => (
                <tr
                  key={item?.id || item?.licenseId || Math.random()}
                  onClick={() => handleRowClick(item)}
                  className={`hover:bg-gray-50 transition-colors duration-200 ${
                    isClickable ? "cursor-pointer" : ""
                  }`}
                >
                  {columns.map((column, index) => (
                    <td key={column?.key || index} className={cellStyles.base}>
                      {renderCell(item, column, index === 0)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(hasData || isLoading) && (
        <div className="border-t border-gray-200 flex-shrink-0">
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={onPageChange}
            itemName={itemName}
          />
        </div>
      )}
    </div>
  );
};

export default DataTable;